import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { Button, Card, ConfirmButton, Input, cn } from "@clevis/shared-ui";
import { call } from "../../lib/callFunction";
import { textMatches } from "../../lib/filter";
import { useAuth } from "../../hooks/useAuth";

// Builder cleanup: FieldPulse kept one customer per development ("KB Homes"
// ×37, each named after a development). Clevis makes that one customer with
// a project per development, and moves every job, estimate and invoice with
// it. The admin reviews the plan, unticks anything that isn't right, applies,
// and can undo a run.

interface Child {
  id: string;
  name: string;
  projectName: string | null;
  jobs: number;
  openJobs: number;
  estimates: number;
  invoices: number;
  lastAt: number;
}
interface Group {
  key: string;
  builderName: string;
  parentId: string | null;
  children: Child[];
}
interface RunRow {
  id: string;
  status: string;
  startedAt: number;
  byName: string;
  stats: { builders: number; merged: number; projectsCreated: number; jobs: number; estimates: number; invoices: number } | null;
}

const day = (ms: number) => (ms ? new Date(ms).toLocaleDateString(undefined, { month: "short", year: "numeric" }) : "never");

export function BuilderCleanupPage() {
  const { tenantId } = useAuth();
  const [groups, setGroups] = useState<Group[] | null>(null);
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [names, setNames] = useState<Record<string, string>>({});
  const [open, setOpen] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  async function load() {
    if (!tenantId) return;
    setError("");
    try {
      const res = await call<{ groups: Group[]; runs: RunRow[] }>("planBuilderCleanup", { tenantId }, { mutation: false, timeoutMs: 130_000 });
      setGroups(res.groups);
      setRuns(res.runs);
      setPicked(new Set(res.groups.map((g) => g.key)));
      setExcluded(new Set());
      setNames({});
    } catch (err) {
      setError((err as Error).message);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const shown = useMemo(
    () => (groups ?? []).filter((g) => textMatches(search, g.builderName, ...g.children.map((c) => c.name))),
    [groups, search],
  );
  const chosen = (groups ?? []).filter((g) => picked.has(g.key));
  const chosenKids = chosen.flatMap((g) => g.children.filter((c) => !excluded.has(c.id)));
  const totals = {
    builders: chosen.filter((g) => g.children.some((c) => !excluded.has(c.id))).length,
    records: chosenKids.length,
    projects: new Set(chosenKids.filter((c) => c.projectName).map((c) => c.projectName)).size,
    jobs: chosenKids.reduce((s, c) => s + c.jobs, 0),
    docs: chosenKids.reduce((s, c) => s + c.estimates + c.invoices, 0),
  };

  async function apply() {
    if (!tenantId) return;
    setBusy(true);
    setNote("");
    setError("");
    try {
      const res = await call<{ stats: NonNullable<RunRow["stats"]> & { buildersCreated: number } }>("applyBuilderCleanup", {
        tenantId,
        groups: chosen.map((g) => ({
          key: g.key,
          ...(names[g.key]?.trim() && names[g.key]!.trim() !== g.builderName ? { builderName: names[g.key]!.trim() } : {}),
          excludeChildIds: g.children.filter((c) => excluded.has(c.id)).map((c) => c.id),
        })),
      }, { timeoutMs: 560_000 });
      const s = res.stats;
      setNote(
        `Done: ${s.merged} records folded into ${s.builders} builders (${s.buildersCreated} new), ${s.projectsCreated} projects made, ` +
          `${s.jobs} jobs, ${s.estimates} estimates and ${s.invoices} invoices moved.`,
      );
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function undo(runId: string) {
    if (!tenantId) return;
    setBusy(true);
    setNote("");
    setError("");
    try {
      const res = await call<{ restored: number }>("undoBuilderCleanup", { tenantId, runId }, { timeoutMs: 560_000 });
      setNote(`Undone: ${res.restored} records are back as they were.`);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const toggle = (set: Set<string>, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div>
        <Link href="/migration" className="text-xs text-steel hover:text-ink">
          ← Migration
        </Link>
        <h1 className="text-lg font-bold">Clean up builder customers</h1>
        <p className="text-xs text-steel">
          FieldPulse made a separate customer for every development ("KB Homes" with "Desert Skies 25's" as the
          name, again and again). This makes each builder one customer with a project per development, and moves
          their jobs, estimates and invoices. The old records are archived, not deleted, and a run can be undone.
          A project's invoices and estimates sync to QuickBooks Desktop under the builder's sub-customer
          ("KB Homes:Desert Skies"), the way QuickBooks already tracks communities; each project shows its
          QuickBooks name, and you can change it there.
        </p>
      </div>

      {error ? <p className="rounded-lg bg-danger/10 p-2 text-xs text-danger">{error}</p> : null}
      {note ? <p className="rounded-lg bg-success/10 p-2 text-xs text-ink">{note}</p> : null}

      {runs.length > 0 ? (
        <Card className="space-y-1 text-xs">
          <div className="font-semibold text-ink">Past runs</div>
          {runs.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-ink">
                {new Date(r.startedAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                {r.byName ? ` · ${r.byName}` : ""} ·{" "}
                {r.stats ? `${r.stats.merged} records → ${r.stats.builders} builders, ${r.stats.jobs} jobs` : r.status}
                {r.status === "undone" ? " · undone" : ""}
              </span>
              {r.status === "done" ? (
                <ConfirmButton
                  className="px-2 py-0.5 text-xs"
                  busy={busy}
                  confirm="Put everything from this run back?"
                  confirmLabel="Undo"
                  onConfirm={() => void undo(r.id)}
                >
                  Undo
                </ConfirmButton>
              ) : null}
            </div>
          ))}
        </Card>
      ) : null}

      {groups === null ? (
        <p className="text-sm text-steel">{error ? "" : "Reading every customer, job, estimate and invoice…"}</p>
      ) : groups.length === 0 ? (
        <Card>
          <p className="text-sm text-steel">Nothing to clean up: no builder has more than one customer record.</p>
        </Card>
      ) : (
        <>
          <Card className="space-y-2">
            <p className="text-sm text-ink">
              <strong>{totals.records}</strong> records fold into <strong>{totals.builders}</strong> builders as{" "}
              <strong>{totals.projects}</strong> projects, moving <strong>{totals.jobs}</strong> jobs and{" "}
              <strong>{totals.docs}</strong> estimates and invoices.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <ConfirmButton
                variant="primary"
                busy={busy}
                disabled={totals.records === 0}
                confirm={`Fold ${totals.records} records into ${totals.builders} builders?`}
                confirmLabel="Clean up"
                onConfirm={() => void apply()}
              >
                Clean up {totals.builders} builders
              </ConfirmButton>
              <button className="text-xs text-steel underline hover:text-ink" onClick={() => setPicked(new Set(groups.map((g) => g.key)))}>
                Select all
              </button>
              <button className="text-xs text-steel underline hover:text-ink" onClick={() => setPicked(new Set())}>
                Select none
              </button>
              <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => void load()}>
                Refresh
              </Button>
            </div>
          </Card>

          <Input placeholder="Find a builder or development…" value={search} onChange={(e) => setSearch(e.target.value)} />

          <Card className="p-0">
            {shown.map((g) => {
              const kidsIn = g.children.filter((c) => !excluded.has(c.id));
              const isOpen = open === g.key;
              return (
                <div key={g.key} className="border-b border-steel/10 last:border-0">
                  <div className="flex items-center gap-2 px-3 py-1.5">
                    <input
                      type="checkbox"
                      aria-label={`Clean up ${g.builderName}`}
                      checked={picked.has(g.key)}
                      onChange={() => setPicked((s) => toggle(s, g.key))}
                    />
                    <button className="min-w-0 flex-1 text-left" onClick={() => setOpen(isOpen ? null : g.key)}>
                      <span className="text-sm font-semibold text-ink">{names[g.key] || g.builderName}</span>
                      <span className="text-xs text-steel">
                        {" "}
                        · {g.parentId ? "existing customer" : "new customer"} · {kidsIn.length} record{kidsIn.length === 1 ? "" : "s"} ·{" "}
                        {kidsIn.reduce((s, c) => s + c.jobs, 0)} jobs
                      </span>
                    </button>
                    <span className="shrink-0 text-xs text-steel">{isOpen ? "▾" : "▸"}</span>
                  </div>
                  {isOpen ? (
                    <div className="space-y-1 bg-cream/40 px-3 pb-2 pt-1">
                      <label className="flex items-center gap-2 text-xs text-ink">
                        Builder name
                        <Input
                          className="max-w-xs py-1 text-xs"
                          value={names[g.key] ?? g.builderName}
                          onChange={(e) => setNames((n) => ({ ...n, [g.key]: e.target.value }))}
                        />
                      </label>
                      {g.children.map((c) => (
                        <label key={c.id} className={cn("flex cursor-pointer items-center gap-2 text-xs", excluded.has(c.id) && "opacity-50")}>
                          <input type="checkbox" checked={!excluded.has(c.id)} onChange={() => setExcluded((s) => toggle(s, c.id))} />
                          <span className="min-w-0 flex-1 truncate text-ink">
                            {c.projectName ? (
                              <>
                                project <strong>{c.projectName}</strong>
                              </>
                            ) : (
                              <>
                                duplicate of the builder: <strong>merge</strong>
                              </>
                            )}
                          </span>
                          <span className="shrink-0 text-steel">
                            {c.jobs} jobs{c.openJobs ? ` (${c.openJobs} open)` : ""} · {c.estimates + c.invoices} docs · last {day(c.lastAt)}
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
            {shown.length === 0 ? <p className="px-4 py-6 text-center text-sm text-steel">No builder matches.</p> : null}
          </Card>
        </>
      )}
    </div>
  );
}
